/**
 * Authentication state for the whole app, backed by Cognito via Amplify Auth.
 * The role comes from the `cognito:groups` claim of the ID token; the backend uses the same claim.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  confirmResetPassword as amplifyConfirmResetPassword,
  resetPassword as amplifyResetPassword,
  confirmSignIn as amplifyConfirmSignIn,
  confirmSignUp as amplifyConfirm,
  fetchAuthSession,
  resendSignUpCode,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
} from 'aws-amplify/auth'
import { isConfigured } from '@/lib/config'
import type { Role } from '@/lib/types'

export interface AuthUser {
  id: string
  email: string
  name: string
  role: Role | null
}

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  signIn(email: string, password: string): Promise<void>
  /** Passwordless: Cognito emails a one-time code; finish with confirmSignInCode. */
  signInWithCode(email: string): Promise<void>
  confirmSignInCode(code: string): Promise<void>
  signUp(email: string, password: string, name: string, role: Role): Promise<'confirm' | 'done'>
  confirm(email: string, code: string): Promise<void>
  resendCode(email: string): Promise<void>
  requestPasswordReset(email: string): Promise<void>
  completePasswordReset(email: string, code: string, newPassword: string): Promise<void>
  signOut(): Promise<void>
  /** The current ID token; `forceRefresh` asks Cognito for a new one even if this browser thinks it is still valid. */
  getToken(options?: { forceRefresh?: boolean }): Promise<string | null>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function parseGroups(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String)
  if (typeof raw === 'string') return raw.replace(/[[\]]/g, '').split(/[,\s]+/).filter(Boolean)
  return []
}

async function loadUser(): Promise<AuthUser | null> {
  if (!isConfigured) return null
  try {
    const session = await fetchAuthSession()
    const payload = session.tokens?.idToken?.payload
    if (!payload?.sub) return null
    const groups = parseGroups(payload['cognito:groups'])
    const role: Role | null = groups.includes('engineer') ? 'engineer' : groups.includes('sme') ? 'sme' : null
    return { id: String(payload.sub), email: String(payload.email ?? ''), name: String(payload.name ?? ''), role }
  } catch {
    return null
  }
}

/** Map Cognito reset failures to actionable messages. */
function passwordResetError(error: unknown): Error {
  const name = (error as { name?: string })?.name
  switch (name) {
    case 'CodeMismatchException': return new Error('That code is not right. Check the email and try again.')
    case 'ExpiredCodeException': return new Error('That code has expired. Request a new reset code.')
    case 'LimitExceededException':
    case 'TooManyRequestsException': return new Error('Too many attempts. Wait a minute and try again.')
    case 'InvalidPasswordException': return new Error('Use at least 10 characters with upper and lower case letters and a number.')
    case 'PasswordHistoryPolicyViolationException': return new Error('Choose a password you have not used before.')
    case 'UserNotFoundException':
    case 'NotAuthorizedException': return new Error('Unable to reset the password. Check your email or sign in with a code.')
    case 'UserNotConfirmedException': return new Error('Confirm your email before resetting your password.')
    case 'InvalidParameterException': return new Error('Password reset requires a confirmed account with a verified email. Confirm your email or contact support.')
    default: return new Error('Unable to reset the password right now. Check your connection and try again.')
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadUser().then((u) => { setUser(u); setLoading(false) })
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await amplifySignIn({ username: email, password })
    if (!res.isSignedIn) throw new Error(`Additional step required: ${res.nextStep.signInStep}`)
    setUser(await loadUser())
  }, [])

  const signInWithCode = useCallback(async (email: string) => {
    const res = await amplifySignIn({ username: email, options: { authFlowType: 'USER_AUTH', preferredChallenge: 'EMAIL_OTP' } })
    if (res.isSignedIn) { setUser(await loadUser()); return }
    if (res.nextStep.signInStep !== 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') throw new Error(`Unexpected sign-in step: ${res.nextStep.signInStep}`)
  }, [])

  const confirmSignInCode = useCallback(async (code: string) => {
    const res = await amplifyConfirmSignIn({ challengeResponse: code.trim() })
    if (!res.isSignedIn) throw new Error(`Additional step required: ${res.nextStep.signInStep}`)
    setUser(await loadUser())
  }, [])

  const signUp = useCallback(async (email: string, password: string, name: string, role: Role) => {
    const res = await amplifySignUp({
      username: email,
      password,
      options: { userAttributes: { email, name, 'custom:role': role } },
    })
    return res.nextStep.signUpStep === 'CONFIRM_SIGN_UP' ? 'confirm' : 'done'
  }, [])

  const confirm = useCallback(async (email: string, code: string) => {
    await amplifyConfirm({ username: email, confirmationCode: code })
  }, [])

  const resendCode = useCallback(async (email: string) => { await resendSignUpCode({ username: email }) }, [])

  const requestPasswordReset = useCallback(async (email: string) => {
    let result
    try { result = await amplifyResetPassword({ username: email.trim().toLowerCase() }) }
    catch (error) { throw passwordResetError(error) }
    if (result.nextStep.resetPasswordStep !== 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
      throw new Error('Password reset is not available right now. Try signing in or contact support.')
    }
  }, [])

  const completePasswordReset = useCallback(async (email: string, code: string, newPassword: string) => {
    try { await amplifyConfirmResetPassword({ username: email.trim().toLowerCase(), confirmationCode: code.trim(), newPassword }) }
    catch (error) { throw passwordResetError(error) }
  }, [])

  const signOut = useCallback(async () => { await amplifySignOut(); setUser(null) }, [])

  const getToken = useCallback(async (options?: { forceRefresh?: boolean }) => {
    if (!isConfigured) return null
    const session = await fetchAuthSession({ forceRefresh: options?.forceRefresh })
    return session.tokens?.idToken?.toString() ?? null
  }, [])

  const value = useMemo(() => ({ user, loading, signIn, signInWithCode, confirmSignInCode, signUp, confirm, resendCode, requestPasswordReset, completePasswordReset, signOut, getToken }),
    [user, loading, signIn, signInWithCode, confirmSignInCode, signUp, confirm, resendCode, requestPasswordReset, completePasswordReset, signOut, getToken])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

/** Module-level accessor for non-React code (the API client). Set by TokenBridge. */
let tokenGetter: AuthContextValue['getToken'] = async () => null
export const getAuthToken: AuthContextValue['getToken'] = (options) => tokenGetter(options)
export function TokenBridge() {
  const { getToken } = useAuth()
  useEffect(() => { tokenGetter = getToken }, [getToken])
  return null
}
